import { Calendar, User, Tag, ArrowLeft, Clock, Share2, Phone, Mail, Shield, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import SEOHead from '../../components/SEOHead'

export default function SafetyTips() {
  const shareUrl = window.location.href
  const shareTitle = "Top 10 Safety Tips Every Professional Driver Should Know"

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        })
      } catch (error) {
        console.log('Error sharing:', error)
      }
    }
  }

  const safetyTips = [
    {
      title: "Pre-Journey Vehicle Inspection",
      description: "Conduct a thorough walk-around inspection before every journey. Check tyres, lights, mirrors, and fluid levels. A proper pre-journey check can prevent 90% of vehicle-related incidents."
    },
    {
      title: "Maintain Safe Following Distances",
      description: "Use the 3-second rule as a minimum, extending to 6 seconds in wet conditions. Remember, a loaded HGV can take up to 100 metres to stop from 60mph."
    },
    {
      title: "Plan Your Route and Rest Breaks",
      description: "Plan routes in advance, accounting for bridge heights, weight restrictions, and suitable rest areas. Take a 15-minute break every 2 hours and a 30-minute break every 4.5 hours."
    },
    {
      title: "Monitor Blind Spots Constantly",
      description: "Be aware of your vehicle's blind spots and use all mirrors frequently. Always assume other road users cannot see you, especially cyclists and pedestrians in urban areas."
    },
    {
      title: "Adjust Driving for Weather Conditions",
      description: "Reduce speed and increase following distances in adverse weather. In icy conditions, reduce speed by at least 50% and avoid sudden movements."
    },
    {
      title: "Secure Your Load Properly",
      description: "Ensure loads are properly secured using appropriate restraints. Check load security during journey breaks as shifting cargo can affect vehicle stability and is illegal."
    },
    {
      title: "Stay Alert and Avoid Fatigue",
      description: "Recognise early signs of fatigue: yawning, heavy eyelids, or drifting. If tired, find a safe place to rest immediately. Fatigue is a factor in 20% of road accidents."
    },
    {
      title: "Use Technology Wisely",
      description: "Utilise safety systems like lane departure warnings and collision avoidance, but don't rely on them entirely. Set up sat-nav before starting your journey."
    },
    {
      title: "Maintain Professional Standards",
      description: "Keep your CPC up to date, attend additional safety training courses, and stay informed about new regulations and best practices in the industry."
    },
    {
      title: "Know Emergency Procedures",
      description: "Know how to respond to breakdowns, accidents, and hazardous material spills. Keep emergency contact numbers readily available and ensure your mobile is charged."
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title="Top 10 Safety Tips Every Professional Driver Should Know - C9 Recruitment"
        description="Essential safety practices that can help prevent accidents and keep you safe on the road. Professional HGV driver safety guidelines and best practices."
        keywords="HGV safety, professional driver tips, road safety, truck driving safety, vehicle inspection, driver training"
        canonical="/blog/safety-tips"
      />
      
      {/* Header */}
      <section className="bg-gradient-to-br from-c9-blue to-c9-blue-alt text-white py-16">
        <div className="container-full">
          <div className="flex items-center gap-4 mb-8">
            <Link 
              to="/blog" 
              className="flex items-center gap-2 text-c9-orange hover:text-white transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>15th May 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>7 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Sam Wharfe (CEO, C9 Recruitment)</span>
              </div>
              <span className="bg-c9-orange px-3 py-1 rounded-full text-xs font-medium">Safety</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
              Top 10 <span className="text-c9-orange">Safety Tips</span> Every Professional Driver Should Know
            </h1>
            
            <p className="text-xl mb-8 leading-relaxed opacity-90">
              Essential safety practices that can help prevent accidents and keep you safe on the road. These tips are drawn from industry best practices and real-world experience.
            </p>
            
            <div className="flex items-center gap-4">
              <button 
                onClick={handleShare}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors"
              >
                <Share2 className="h-4 w-4" />
                Share Article
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16">
        <div className="container-full">
          <div className="article-content">
            <div className="article-prose">
              
              <div className="article-warning">
                <div className="flex items-start gap-3">
                  <Shield className="h-6 w-6 text-yellow-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-yellow-800 mb-2">Safety First</h3>
                    <p className="text-yellow-700">Professional drivers handle vehicles that can weigh up to 44 tonnes. Your safety practices don't just protect you – they protect everyone on the road. Never compromise on safety.</p>
                  </div>
                </div>
              </div>

              <h2 className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-c9-orange" />
                The Top 10 Safety Tips
              </h2>
              
              <p>Safety should always be your number one priority when driving professionally. These ten essential tips can help prevent accidents and save lives:</p>

              <div className="safety-tips-grid">
                {safetyTips.map((tip, index) => (
                  <div key={index} className="article-card">
                    <div className="flex items-start gap-4">
                      <div className="bg-c9-orange text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-c9-blue mb-2">{tip.title}</h3>
                        <p className="text-gray-700">{tip.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <h2>Vehicle Inspection Checklist</h2>
              
              <p>A comprehensive pre-journey inspection should include:</p>
              
              <h3>External Checks</h3>
              <ul>
                <li><strong>Tyres:</strong> Check condition, tread depth, and inflation pressure</li>
                <li><strong>Lights:</strong> Test all lights including indicators and hazard lights</li>
                <li><strong>Mirrors:</strong> Ensure all mirrors are clean and properly adjusted</li>
                <li><strong>Body work:</strong> Look for damage that could affect safety</li>
                <li><strong>Load security:</strong> Verify cargo is properly secured</li>
              </ul>

              <h3>Internal Checks</h3>
              <ul>
                <li><strong>Dashboard warnings:</strong> Check for warning lights</li>
                <li><strong>Seat and steering wheel:</strong> Adjust for comfort and control</li>
                <li><strong>Seatbelt:</strong> Ensure it's working properly</li>
                <li><strong>Emergency equipment:</strong> Confirm first aid kit and safety equipment are present</li>
              </ul>

              <h2>Managing Road Risks</h2>
              
              <h3>Weather Conditions</h3>
              <p>Adapt your driving to current conditions:</p>
              <ul>
                <li><strong>Rain:</strong> Reduce speed by 10-15mph and double following distance</li>
                <li><strong>Snow/Ice:</strong> Reduce speed by 50% and avoid sudden movements</li>
                <li><strong>Fog:</strong> Use appropriate lights and maintain extra following distance</li>
                <li><strong>High winds:</strong> Be prepared for sudden gusts, especially on bridges</li>
              </ul>

              <h3>Urban Driving Hazards</h3>
              <ul>
                <li>Cyclists and pedestrians in blind spots</li>
                <li>Narrow streets and tight turns</li>
                <li>Limited parking for rest breaks</li>
                <li>Increased traffic congestion</li>
                <li>Low bridges and weight restrictions</li>
              </ul>

              <div className="article-highlight">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-6 w-6 text-c9-orange mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-c9-blue mb-2">Critical Safety Fact</h3>
                    <p className="text-gray-700">HGVs are involved in approximately 5% of all road accidents but account for 15% of road fatalities. Your professional driving standards directly impact road safety statistics.</p>
                  </div>
                </div>
              </div>

              <h2>Fatigue Management</h2>
              
              <p>Driver fatigue is a serious safety risk that affects reaction times and decision-making:</p>
              
              <h3>Recognition Signs</h3>
              <ul>
                <li>Frequent yawning or blinking</li>
                <li>Difficulty keeping your eyes open</li>
                <li>Drifting from your lane</li>
                <li>Difficulty remembering the last few minutes</li>
                <li>Missing road signs or exits</li>
              </ul>

              <h3>Prevention Strategies</h3>
              <ul>
                <li>Get adequate sleep before starting work (7-9 hours)</li>
                <li>Take regular breaks every 2 hours</li>
                <li>Avoid driving during your natural sleep hours</li>
                <li>Stay hydrated and eat light, healthy meals</li>
                <li>If tired, find a safe place to rest immediately</li>
              </ul>

              <h2>Load Security and Weight Distribution</h2>
              
              <p>Improperly secured loads are dangerous and illegal:</p>
              
              <ul>
                <li><strong>Use appropriate restraints:</strong> Straps, chains, or load bars as required</li>
                <li><strong>Check throughout journey:</strong> Inspect load security during breaks</li>
                <li><strong>Distribute weight evenly:</strong> Improper weight distribution affects handling</li>
                <li><strong>Know your limits:</strong> Don't exceed maximum gross vehicle weight</li>
                <li><strong>Secure loose items:</strong> Ensure nothing can become a projectile during sudden stops</li>
              </ul>

              <h2>Technology and Safety Systems</h2>
              
              <p>Modern vehicles have numerous safety features to assist drivers:</p>
              
              <h3>Common Safety Systems</h3>
              <ul>
                <li><strong>Lane Departure Warning:</strong> Alerts when vehicle drifts from lane</li>
                <li><strong>Collision Avoidance:</strong> Automatically applies brakes if collision detected</li>
                <li><strong>Blind Spot Monitoring:</strong> Warns of vehicles in blind spots</li>
                <li><strong>Electronic Stability Control:</strong> Helps maintain vehicle control</li>
                <li><strong>Adaptive Cruise Control:</strong> Maintains safe following distances</li>
              </ul>
              
              <p>While these systems are valuable safety aids, they should supplement, not replace, good driving practices and constant vigilance.</p>

              <h2>Creating a Safety Culture</h2>
              
              <p>Safety is not just an individual responsibility – it's about creating a culture where safety comes first:</p>
              
              <ul>
                <li>Report near misses and safety concerns to your employer</li>
                <li>Share knowledge and experience with newer drivers</li>
                <li>Participate actively in safety meetings and training</li>
                <li>Challenge unsafe practices when you see them</li>
                <li>Continuously look for ways to improve safety procedures</li>
              </ul>

              <h2>Emergency Preparedness</h2>
              
              <p>Being prepared for emergencies is crucial:</p>
              
              <ul>
                <li>Keep emergency contact numbers readily accessible</li>
                <li>Know the location of emergency equipment on your vehicle</li>
                <li>Understand first aid basics – consider taking a course</li>
                <li>Keep high-visibility clothing and warning triangles accessible</li>
                <li>Know how to safely stop and secure your vehicle in emergencies</li>
              </ul>

              <div className="article-success">
                <div className="flex items-start gap-3">
                  <Shield className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-green-800 mb-2">Remember</h3>
                    <p className="text-green-700">Your safety practices protect not just yourself, but everyone who shares the road with you. Professional drivers are road safety ambassadors – lead by example.</p>
                  </div>
                </div>
              </div>

              <h2>Continuous Improvement</h2>
              
              <p>Safety is an ongoing commitment that requires continuous learning and improvement. Stay updated with industry best practices, attend additional training courses, and always be open to learning new safety techniques.</p>
              
              <p>At C9 Recruitment, we work with employers who prioritise safety and provide comprehensive training and support for their drivers. If you're looking for opportunities with safety-conscious companies, we can help connect you with the right employers.</p>

            </div>
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="bg-c9-blue text-white section-padding">
        <div className="container-full text-center">
          <div className="max-w-3xl mx-auto">
            <div className="article-cta">
              <h3 className="text-2xl font-bold mb-4 flex items-center justify-center gap-2">
                <Shield className="h-6 w-6" />
                Find Safety-Focused Employers
              </h3>
              <p className="text-lg mb-6">
                Looking for opportunities with companies that prioritise driver safety and professional development? We partner with operators who understand that safety isn't just policy – it's culture.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/candidates" 
                  className="flex items-center justify-center gap-2 bg-white text-c9-orange hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors"
                >
                  <ExternalLink className="h-5 w-5" />
                  Browse Safe Working Opportunities
                </Link>
                <a 
                  href="tel:02081524574" 
                  className="flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-c9-blue px-8 py-3 rounded-lg font-medium transition-colors"
                >
                  <Phone className="h-5 w-5" />
                  Speak to Safety Experts
                </a>
              </div>
              
              <div className="mt-8 text-sm text-gray-200">
                <p>Connect with safety-conscious employers who invest in driver training and wellbeing</p>
                <div className="flex items-center justify-center gap-6 mt-3">
                  <a href="tel:02081524574" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Phone className="h-4 w-4" />
                    0208 152 4574
                  </a>
                  <a href="mailto:commercial@c9-recruitment.com" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Mail className="h-4 w-4" />
                    commercial@c9-recruitment.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
